import { useSearchParams } from 'react-router-dom';
import Avatar from '@sdk/components/avatar/Avatar';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text11 from '@sdk/components/ui/typography/Text11';
import gettext from '@sdk/lang/gettext';
import { useSession } from '@sdk/session/SessionContext';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';

import { useParsedContent } from '@web/common/hooks/useParsedContent';
import { SearchContentPreview } from '@web/components/SearchContentPreview/SearchContentPreview';
import {
  Wrapper,
  SearchItemHeader,
} from '@web/conversations/ConversationSearch/ConversationSearchItem/conversationSearchItem.styles';
import { MessagePost } from '@web/conversations/types/Message';
import { useNavigate } from '@web/Router/router.hooks';
import { useGetConversations } from '@web/conversations/hooks/useGetConversations';

type Props = {
  message: MessagePost;
};

export const SEARCH_ITEM_CONTENT_HEIGHT = 40;

export const ConversationSearchItem = ({ message }: Props) => {
  const { currentUser } = useSession();
  const { data } = useGetConversations();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('query');
  const parsedContent = useParsedContent(message.content, searchQuery);
  const navigate = useNavigate();

  if (!message.content) {
    return null;
  }

  const { usTimeFormat } = currentUser.settings;
  const date = dateToHumanFormat(message.postedAt);
  const time = formatTime(message.postedAt, usTimeFormat);
  const navigateToMessage = () => {
    navigate.toConversationDetails({
      params: {
        id: message.conversationId,
      },
      queryParams: {
        messageId: message.id,
      },
    });
  };

  const parentConversation = data.find(
    (conversation) => conversation.id === message.conversationId
  );
  const conversationName = parentConversation?.name || '';

  return (
    <Wrapper onClick={navigateToMessage}>
      <Avatar user={message.author} />
      <div>
        <SearchItemHeader>
          <Text11 italic themeColor={ThemeColorType.iconGray}>
            <TranslateWithComponents
              replaceComponents={{
                authorName: (
                  <Text14 bold themeColor={ThemeColorType.gray}>
                    {message.author.fullname}
                  </Text14>
                ),
              }}>
              {gettext('[[authorName]] on %(date)s at %(time)s in %(conversationName)s', {
                date,
                time,
                conversationName,
              })}
            </TranslateWithComponents>
          </Text11>
        </SearchItemHeader>
        <SearchContentPreview
          isFixedHeight
          isPreview
          parsedContent={parsedContent}
          previewHeight={SEARCH_ITEM_CONTENT_HEIGHT}
        />
      </div>
    </Wrapper>
  );
};
